import type { MouseEventHandler, ReactNode } from 'react';

import {
  Button,
  Dialog as DialogPrimitive,
  DialogContent as DialogContentPrimitive
} from 'design-system';

import { cn } from '@/lib/utils';

type DialogButtonVariant = 'contained' | 'outlined' | 'text';

type DialogProps = {
  /** Controls whether the dialog is mounted and visible. */
  open: boolean;
  /** Called for Escape, backdrop click (unless disabled) and explicit close actions. */
  onClose: () => void;
  /** Optional header title rendered above the dialog body. */
  title?: string;
  /** Main dialog body content. */
  children: ReactNode;
  /** Optional action row rendered below the body content. */
  buttons?: ReactNode;
  /** Shows the header close icon and wires it to onClose. */
  closeIcon?: boolean;
  /** Prevents backdrop clicks from calling onClose while keeping Escape dismissal. */
  disableBackdropClick?: boolean;
  /** Classes merged onto the dialog popup (sizing, background, transition, etc). */
  className?: string;
  /** Classes merged onto the optional action row. */
  actionsClassName?: string;
};

const BUTTON_VARIANT_MAP: Record<DialogButtonVariant, 'default' | 'outline' | 'ghost'> = {
  contained: 'default',
  outlined: 'outline',
  text: 'ghost'
};

export const Dialog = ({
  open,
  onClose,
  title,
  children,
  buttons,
  closeIcon,
  disableBackdropClick,
  className,
  actionsClassName
}: DialogProps) => (
  <DialogPrimitive
    disablePointerDismissal={disableBackdropClick}
    open={open}
    onOpenChange={(next: boolean) => {
      if (!next) onClose();
    }}
  >
    <DialogContentPrimitive
      className={cn('sm:max-w-[610px] bg-gx-main-background text-gx-text', className)}
      showCloseButton={Boolean(closeIcon)}
    >
      {title && <p className='mb-3 text-sm font-medium'>{title}</p>}
      {children}
      {buttons && <div className={cn('mt-4', actionsClassName)}>{buttons}</div>}
    </DialogContentPrimitive>
  </DialogPrimitive>
);

export const DialogButton = ({
  onClick,
  children,
  variant = 'contained',
  ...rest
}: {
  variant?: DialogButtonVariant;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}) => (
  <Button
    className='h-[33px] min-w-[120px]'
    variant={BUTTON_VARIANT_MAP[variant]}
    onClick={onClick}
    {...rest}
  >
    {children}
  </Button>
);

export const DialogContentText = ({ children }: { children: ReactNode }) => (
  <p className='text-base'>{children}</p>
);
